import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FeedbackItem, FeedbackType, submitFeedback, updateFeedback } from '../../services/api/feedbackService';
import { useTheme } from '../../context/ThemeContext';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedback?: FeedbackItem | null;
  onSaved?: () => void;
}

const FEEDBACK_TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: FeedbackType.Bug, label: 'Report a Bug' },
  { value: FeedbackType.Suggestion, label: 'Suggestion' },
  { value: FeedbackType.Rating, label: 'Rate the App' },
  { value: FeedbackType.General, label: 'General Feedback' },
];

const FEEDBACK_TYPE_NAME_TO_VALUE: Record<string, FeedbackType> = {
  Bug: FeedbackType.Bug,
  Suggestion: FeedbackType.Suggestion,
  General: FeedbackType.General,
  Rating: FeedbackType.Rating,
};

function resolveFeedbackType(typeName?: string): FeedbackType {
  if (!typeName) return FeedbackType.General;
  const normalized = typeName.trim().toLowerCase();
  const match = Object.keys(FEEDBACK_TYPE_NAME_TO_VALUE).find(
    (key) => key.toLowerCase() === normalized
  );
  return match ? FEEDBACK_TYPE_NAME_TO_VALUE[match] : FeedbackType.General;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, feedback, onSaved }) => {
  const { theme } = useTheme();
  const isEditMode = !!feedback;

  const [feedbackType, setFeedbackType] = useState<FeedbackType>(() =>
    resolveFeedbackType(feedback?.feedbackTypeName)
  );
  const [subject, setSubject] = useState(() => feedback?.subject || '');
  const [message, setMessage] = useState(() => feedback?.message || '');
  const [rating, setRating] = useState<number | null>(() => feedback?.rating ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (feedback) {
      setFeedbackType(resolveFeedbackType(feedback.feedbackTypeName));
      setSubject(feedback.subject || '');
      setMessage(feedback.message || '');
      setRating(feedback.rating ?? null);
    } else {
      setFeedbackType(FeedbackType.General);
      setSubject('');
      setMessage('');
      setRating(null);
    }
    setError('');
    setSuccess(false);
  }, [isOpen, feedback]);

  if (!isOpen) return null;

  const resetState = () => {
    setFeedbackType(FeedbackType.General);
    setSubject('');
    setMessage('');
    setRating(null);
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetState();
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      setError('Please provide your feedback message.');
      return;
    }

    if (feedbackType === FeedbackType.Rating && !rating) {
      setError('Please select a rating.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const result = isEditMode
        ? await updateFeedback(feedback!.feedbackId, {
            feedbackType,
            subject: subject.trim() || undefined,
            message: message.trim(),
            rating: feedbackType === FeedbackType.Rating ? rating ?? undefined : undefined,
          })
        : await submitFeedback({
            feedbackType,
            subject: subject.trim() || undefined,
            message: message.trim(),
            rating: feedbackType === FeedbackType.Rating ? rating ?? undefined : undefined,
          });

      if (!result.success) {
        setError(result.message || 'Failed to save feedback. Please try again.');
        return;
      }

      setSuccess(true);
      onSaved?.();
      setTimeout(() => {
        resetState();
        onClose();
      }, 1500);
    } catch (err) {
      setError('Failed to save feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{isEditMode ? 'Edit Feedback' : 'Send Feedback'}</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {success ? (
          <div className="p-6">
            <p className="text-sm text-green-600">
              {isEditMode ? 'Your feedback has been updated!' : 'Thank you for your feedback!'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              We'd love to hear your thoughts. Let us know what's working, what's not, or what you'd like to see.
            </p>

            <div className="mb-4">
              <label htmlFor="feedbackType" className="block text-sm font-medium text-gray-700 mb-2">
                Feedback Type <span className="text-red-500">*</span>
              </label>
              <Select
                value={String(feedbackType)}
                onValueChange={(value) => setFeedbackType(Number(value) as FeedbackType)}
                disabled={isSubmitting}
              >
                <SelectTrigger
                  className="w-full bg-white"
                  style={{ border: `1px solid ${theme.border}`, borderRadius: '0.5rem', color: theme.text }}
                >
                  <SelectValue placeholder="Select feedback type">
                    {FEEDBACK_TYPE_OPTIONS.find((opt) => opt.value === feedbackType)?.label}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {FEEDBACK_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {feedbackType === FeedbackType.Rating && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Rating <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      disabled={isSubmitting}
                      className={`text-2xl ${rating && star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
                      aria-label={`${star} star`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="feedbackSubject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                id="feedbackSubject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Short summary (optional)"
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                maxLength={200}
                style={{ backgroundColor: theme.surfaceHover, borderColor: theme.border, color: theme.text }}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="feedbackMessage" className="block text-sm font-medium text-gray-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="feedbackMessage"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's on your mind..."
                rows={5}
                disabled={isSubmitting}
                className="w-full max-h-40 overflow-y-auto resize-none rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                maxLength={2000}
                style={{ backgroundColor: theme.surfaceHover, borderColor: theme.border, color: theme.text }}
              />
              <p className="text-xs text-gray-500 mt-1">{message.length}/2000 characters</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !message.trim()}
                className="flex-1 rounded-lg bg-brand-primary px-4 py-2 font-medium text-white transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting
                  ? isEditMode ? 'Saving...' : 'Submitting...'
                  : isEditMode ? 'Save Changes' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
